import { ColDef, GridReadyEvent, ICellRendererParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  BaseService,
  GridOptionsExt,
  ISortModel,
  ProxAgFns,
  RowSelectionMultipleCheckbox,
  RowSelectionSingle,
  SaveAndQueryComponent,
} from '@core';
import { EntityFns } from '@data';
import {
  Account,
  NotificationEvent,
  NotificationEventAccountMap,
  NotificationEventEnum,
  NotificationTemplate,
  NotificationTypeEnum,
  ProximityRightEnum,
} from '@models';
import { StringFns, UtilFns } from '@utils';
import { MenuItem } from 'primeng/api';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { NotificationTemplateDialogComponent } from './notification-template-dialog.component';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { FileUploadHandlerEvent } from 'primeng/fileupload';

export interface TemplateType {
  id: TemplateTypeEnum;
  name: string;
}

export enum TemplateTypeEnum {
  FreeForm = 1,
  Event = 2,
  All = 3,
}

/** Represents a row in the Event-Centric View grid */
export interface EventRow {
  eventId: NotificationEventEnum;
  eventName: string;
  eventDescription: string;
  eventLongDescription: string;
  recipientDescription: string;
  notificationEvent: NotificationEvent;
  emailTemplate?: NotificationTemplate;
  smsTemplate?: NotificationTemplate;
  inAppTemplate?: NotificationTemplate;
  isGlobal: boolean; // true if ALL templates for this event are global
}

@Component({
  selector: 'prox-account-notification-template-list',
  templateUrl: './account-notification-template-list.component.html',
  standalone: false,
})
export class AccountNotificationTemplateListComponent extends SaveAndQueryComponent {
  accountId!: string;
  account?: Account;
  accountMaps?: NotificationEventAccountMap[];
  allTemplates: NotificationTemplate[] = [];
  /** Master list of NotificationEvents (server-side enum-backed). Drives the Event-Centric grid
   * so newly-defined events show up immediately, even before any template has been created. */
  allEvents: NotificationEvent[] = [];
  templates: NotificationTemplate[] = []; // Used for FreeForm view (flat list)
  eventRows: EventRow[] = []; // Used for Event-Centric view (grouped by event)
  menuItems: MenuItem[] = [];
  gridOptions: GridOptionsExt = {};
  isGlobalAdmin: boolean = false;

  /** When false (default), inactive free-form templates are hidden from the FreeForm grid. */
  showInactive: boolean = false;

  // Email wrapper templates (Header/Footer) - displayed separately from events
  emailHeaderTemplate?: NotificationTemplate;
  emailFooterTemplate?: NotificationTemplate;

  // Expose enum to template
  NotificationEventEnum = NotificationEventEnum;

  templateType!: TemplateType;

  TemplateTypeEvent = { id: TemplateTypeEnum.Event, name: 'Event based' } as TemplateType;
  TemplateTypeFreeForm = { id: TemplateTypeEnum.FreeForm, name: 'Free form' } as TemplateType;
  templateTypes = [this.TemplateTypeEvent, this.TemplateTypeFreeForm];

  // Special event IDs that should not appear in the main event grid
  private readonly EMAIL_WRAPPER_EVENTS = [NotificationEventEnum.EmailHeader, NotificationEventEnum.EmailFooter];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    public override dbSaveService: AccountDbSaveService,
    public override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.isGlobalAdmin = this.baseService.authService.getUser()?.hasRight(ProximityRightEnum.IsGlobalAdminForAccounts) ?? false;

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,
      rowSelection: this.isGlobalAdmin ? RowSelectionMultipleCheckbox : RowSelectionSingle,
      getRowId: undefined, // couldn't refresh rows without setting this
    });

    this.templateType = this.templateTypes[0];
    this.account = await this.dbQueryService.getAccountById(this.accountId);

    await Promise.all([this.loadTemplates(), this.loadAccountMaps()]);

    AgFns.captureGridRouteParams(this.gridOptions, this.route, 'id');
    this.setTitle('Notification Templates for ' + this.account.name);
    this.isPageReady = true;
  }

  private async loadTemplates() {
    this.allTemplates = await this.dbQueryService.getNotificationTemplates(this.accountId);
    // Load the master event list once. Subsequent template reloads reuse it.
    if (this.allEvents.length === 0) {
      this.allEvents = await this.dbQueryService.getAll(NotificationEvent);
    }
    this.onTemplateTypeChange();
  }

  onTemplateTypeChange() {
    if (this.templateType.id == TemplateTypeEnum.FreeForm) {
      // FreeForm view: flat list of free-form templates.
      // Hide inactive templates unless the user has opted in via the "Show Inactive Templates" checkbox.
      this.templates = this.allTemplates
        .filter(NotificationTemplate.getTemplateTypeFilter(true))
        // TODO: drop the `any` cast once the Breeze client model is regenerated with the isActive field.
        .filter((t) => this.showInactive || (t as any).isActive !== false);
      this.gridOptions.apiX?.setGridOption('rowData', this.templates);
    } else {
      // Event-Centric view: grouped by event
      this.buildEventRows();
      this.gridOptions.apiX?.setGridOption('rowData', this.eventRows);
    }
    // Reinitialize columns when switching views
    if (this.gridOptions.apiX) {
      this.onGridReady({ api: this.gridOptions.apiX, context: { gridOptions: this.gridOptions } } as any);
    }
  }

  /**
   * Build EventRow array. Seeded from the master NotificationEvents list so newly-defined
   * events appear in the grid immediately, even before any template has been created for them.
   * Then attaches matching templates per event, respecting account-specific vs. global precedence.
   */
  private buildEventRows() {
    const eventTemplates = this.allTemplates.filter(
      NotificationTemplate.getTemplateTypeFilter(false), // Event-based templates only
    );

    // Extract Email Wrapper templates (Header/Footer) - these are displayed separately
    this.emailHeaderTemplate = undefined;
    this.emailFooterTemplate = undefined;

    for (const template of eventTemplates) {
      // Email Wrapper templates must have NotificationTypeId = Email (0)
      if (template.notificationTypeId !== NotificationTypeEnum.Email) {
        continue;
      }

      if (template.notificationEventId === NotificationEventEnum.EmailHeader) {
        // Prefer account-specific over global
        if (!this.emailHeaderTemplate || template.accountId) {
          this.emailHeaderTemplate = template;
        }
      } else if (template.notificationEventId === NotificationEventEnum.EmailFooter) {
        // Prefer account-specific over global
        if (!this.emailFooterTemplate || template.accountId) {
          this.emailFooterTemplate = template;
        }
      }
    }

    // Seed the event map from the master NotificationEvents list. Skip wrapper events
    // (displayed separately) and FreeForm (admin-driven send-to-user flow, not event-centric).
    const eventMap = new Map<NotificationEventEnum, EventRow>();
    for (const evt of this.allEvents) {
      if (this.EMAIL_WRAPPER_EVENTS.includes(evt.id)) continue;
      if (evt.id === NotificationEventEnum.FreeForm) continue;
      eventMap.set(evt.id, {
        eventId: evt.id,
        eventName: evt.name ?? '',
        eventDescription: evt.description ?? '',
        eventLongDescription: evt.longDescription ?? '',
        recipientDescription: evt.recipientDescription ?? '',
        notificationEvent: evt,
        isGlobal: true,
      });
    }

    // Attach any existing templates to their event row, respecting global vs account-specific
    // precedence. Account-specific templates override globals when present.
    for (const template of eventTemplates) {
      const eventId = template.notificationEventId!;
      const row = eventMap.get(eventId);
      if (!row) continue; // template references an unknown or wrapper event — skip

      // For super users: ONLY show global templates (ignore account-specific overrides)
      // For account admins: Prefer account-specific template over global template
      const shouldUseTemplate = this.isGlobalAdmin ? !template.accountId : !template.accountId || template.accountId === this.accountId;
      if (!shouldUseTemplate) continue;

      switch (template.notificationTypeId) {
        case NotificationTypeEnum.Email:
          if (!row.emailTemplate || template.accountId) {
            row.emailTemplate = template;
          }
          break;
        case NotificationTypeEnum.SMS:
          if (!row.smsTemplate || template.accountId) {
            row.smsTemplate = template;
          }
          break;
        case NotificationTypeEnum.InApp:
          if (!row.inAppTemplate || template.accountId) {
            row.inAppTemplate = template;
          }
          break;
      }

      // If any template is account-specific, the row is not fully global
      if (template.accountId) {
        row.isGlobal = false;
      }
    }

    this.eventRows = Array.from(eventMap.values()).sort((a, b) => a.eventDescription.localeCompare(b.eventDescription));
  }

  private async loadAccountMaps() {
    this.accountMaps = await this.dbQueryService.getNotificationEventAccountMaps(this.accountId);
  }

  /** Get the menu of template variables */
  // private loadMenuItems() {
  //   return this.templatesService.getTemplateMenuItems().then(x => { this.menuItems = x; });
  // }

  onGridReady(event: GridReadyEvent) {
    if (this.templateType.id === TemplateTypeEnum.FreeForm) {
      this.initFreeFormGrid(event);
    } else {
      this.initEventCentricGrid(event);
    }
  }

  /** Initialize grid for FreeForm view (flat list) */
  private initFreeFormGrid(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createIconProps('Global', '', () => null, {
          label: '',
          getIcon: this.getIsGlobalIcon.bind(this),
        }),
        ...AgFns.fixWidth(80),
        suppressHeaderMenuButton: true,
      },
      {
        ...AgFns.createButtonProps(' ', this.onEdit.bind(this), {
          label: 'Edit',
          canDisplay: (item) => !!item.accountId || this.isGlobalAdmin,
        }),
        ...AgFns.fixWidth(60),
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'Template Name',
        field: 'templateName',
        sortable: true,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Notification Type',
        field: 'notificationType.name',
        sortable: true,
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Template',
        field: 'template',
        filter: 'agTextColumnFilter',
        valueFormatter: (p) => StringFns.stripHtmlTags(p.value),
        ...AgFns.flexWidth(200),
      },
      { headerName: 'isGlobal', valueGetter: (p) => p.data.accountId ?? '', hide: true },
      {
        ...AgFns.createIconProps('Active', '', this.onToggleActive.bind(this), {
          label: '',
          getIcon: this.getIsActiveIcon.bind(this),
          canDisplay: this.canToggleActive.bind(this),
        }),
        ...AgFns.fixWidth(80),
        suppressHeaderMenuButton: true,
      },
      ProxAgFns.getEntityDeleteColDef(this.onDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];

    const sortModel: ISortModel = [
      { colId: 'isGlobal', sort: 'desc' },
      { colId: 'templateName', sort: 'asc' },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** Initialize grid for Event-Centric view (grouped by event) */
  private initEventCentricGrid(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        headerName: 'Notification Event',
        field: 'eventDescription',
        sortable: true,
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(300),
      },
      {
        headerName: 'Description',
        field: 'eventLongDescription',
        sortable: false,
        // The sentence is longer than any sensible column, so the cell truncates and the
        // tooltip carries the rest. Not editable, so AgFns leaves no tooltip of its own.
        tooltipValueGetter: (params) => (params.data as EventRow)?.eventLongDescription,
        ...AgFns.flexWidth(420),
      },
      {
        headerName: 'Sent To',
        field: 'recipientDescription',
        sortable: true,
        ...AgFns.fixWidth(250),
      },
      {
        headerName: 'Email',
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<EventRow>) => {
          return this.renderTemplateCell(params.data?.emailTemplate, NotificationTypeEnum.Email, params.data);
        },
      },
      {
        headerName: 'SMS',
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<EventRow>) => {
          return this.renderTemplateCell(params.data?.smsTemplate, NotificationTypeEnum.SMS, params.data);
        },
      },
      {
        headerName: 'In-App',
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<EventRow>) => {
          return this.renderTemplateCell(params.data?.inAppTemplate, NotificationTypeEnum.InApp, params.data);
        },
      },
    ];

    const sortModel: ISortModel = [{ colId: 'eventDescription', sort: 'asc' }];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** Render a cell for a notification type template */
  private renderTemplateCell(
    template: NotificationTemplate | undefined,
    typeId: NotificationTypeEnum,
    eventRow: EventRow | undefined,
  ): HTMLElement {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.height = '100%';
    container.style.gap = '4px';

    // Check if template is empty (no actual content after stripping HTML)
    const isTemplateEmpty = (tmpl: NotificationTemplate | undefined): boolean => {
      if (!tmpl) return true;
      const content = tmpl.template || '';
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      return textContent.length === 0;
    };

    // If there's no account-specific template and the global template is empty, treat as "no template"
    if (template && !template.accountId && isTemplateEmpty(template)) {
      template = undefined;
    }

    if (template) {
      // Template exists - show edit button with status indicator
      const isGlobal = !template.accountId;

      const editButton = document.createElement('button');
      editButton.className = 'p-button p-button-text p-button-sm';
      editButton.style.padding = '4px 8px';
      editButton.title = isGlobal ? 'Global template (click to edit/override)' : 'Account-specific template';

      const editIcon = document.createElement('i');
      editIcon.className = isGlobal ? 'pi pi-globe' : 'pi pi-pencil';
      editIcon.style.fontSize = '0.9rem';
      editIcon.style.color = isGlobal ? '#6b7280' : '#10b981'; // gray for global, green for account-specific
      editButton.appendChild(editIcon);

      editButton.addEventListener('click', () => this.onEditEventTemplate(template, typeId, eventRow!));
      container.appendChild(editButton);

      // Add delete button for account-specific templates
      if (!isGlobal) {
        const deleteButton = document.createElement('button');
        deleteButton.className = 'p-button p-button-text p-button-sm';
        deleteButton.style.padding = '4px 8px';
        deleteButton.title = 'Delete account-specific template (reverts to global)';

        const deleteIcon = document.createElement('i');
        deleteIcon.className = 'pi pi-trash';
        deleteIcon.style.fontSize = '0.9rem';
        deleteIcon.style.color = '#ef4444'; // red
        deleteButton.appendChild(deleteIcon);

        deleteButton.addEventListener('click', () => this.onDeleteEventTemplate(template, eventRow!));
        container.appendChild(deleteButton);
      }
    } else {
      // No template - show add button
      const button = document.createElement('button');
      button.className = 'p-button p-button-text p-button-sm';
      button.style.padding = '4px 8px';
      button.title = 'Add template';

      const icon = document.createElement('i');
      icon.className = 'pi pi-plus';
      icon.style.fontSize = '0.9rem';
      icon.style.color = '#9ca3af'; // light gray
      button.appendChild(icon);

      button.addEventListener('click', () => this.onAddEventTemplate(typeId, eventRow!));
      container.appendChild(button);
    }

    return container;
  }

  getIsGlobalIcon(row: NotificationTemplate) {
    if (!row.accountId) {
      return 'pi pi-check';
    } else {
      return '';
    }
  }

  /** Active = green check; Inactive = grey ban icon. Tooltip on the cell explains the toggle. */
  getIsActiveIcon(row: NotificationTemplate) {
    // TODO: drop the `any` cast once the Breeze client model is regenerated with the isActive field.
    return (row as any).isActive !== false ? 'pi pi-check-circle text-emerald-500' : 'pi pi-ban text-gray-400';
  }

  onRowSelected() {}

  canAdd() {
    // Only show Add button for FreeForm view - Event-Centric view adds via grid cells
    return this.templateType.id === TemplateTypeEnum.FreeForm;
  }

  /** Check if we're in Event-Centric view */
  isEventView() {
    return this.templateType.id === TemplateTypeEnum.Event;
  }

  async onEdit(notificationTemplate: NotificationTemplate) {
    const dlg = this.baseService.dialogService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
      notificationTemplate,
      isEditable: true,
      accountId: this.accountId,
      dbSaveService: this.dbSaveService,
    });
    if (ok) {
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      this.gridOptions.apiX?.refreshCells();
    } else {
      notificationTemplate.entityAspect.rejectChanges();
      this.gridOptions.apiX?.refreshCells();
    }
  }

  /** Edit a template from the Event-Centric view */
  async onEditEventTemplate(template: NotificationTemplate, typeId: NotificationTypeEnum, eventRow: EventRow) {
    const isGlobal = !template.accountId;

    if (isGlobal && !this.isGlobalAdmin) {
      // Non-super users editing global template should create account-specific override
      const override = this.dbSaveService.createEntity(NotificationTemplate, {
        accountId: this.accountId,
        notificationEventId: eventRow.eventId,
        notificationTypeId: typeId,
        template: template.template, // Copy the global template content as starting point
      });
      await this.editNewEventTemplate(override, eventRow, typeId);
    } else {
      // Edit the existing template directly
      const dlg = this.baseService.dialogService.pngDialogService;
      const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
        notificationTemplate: template,
        isEditable: true,
        accountId: this.accountId,
        dbSaveService: this.dbSaveService,
      });
      if (ok) {
        await this.dbSaveService.saveSelectedChanges([template]);
        this.buildEventRows();
        this.gridOptions.apiX?.setGridOption('rowData', this.eventRows);
      } else {
        template.entityAspect.rejectChanges();
      }
    }
  }

  /** Delete an account-specific template from the Event-Centric view (reverts to global) */
  async onDeleteEventTemplate(template: NotificationTemplate, eventRow: EventRow) {
    const typeName = NotificationTypeEnum[template.notificationTypeId!];
    const eventName = eventRow.eventDescription;

    const ok = await this.baseService.dialogService.askIfOk(
      'Delete Account-Specific Template',
      `Are you sure you want to delete the account-specific ${typeName} template for "${eventName}"? This will revert to using the global template.`,
    );

    if (ok) {
      EntityFns.deleteOrDetach(template.entityAspect);
      await this.dbSaveService.saveSelectedChanges([template]);
      await this.loadTemplates(); // Reload to refresh the view with global template
    }
  }

  /** Add a new template for an event from the Event-Centric view */
  async onAddEventTemplate(typeId: NotificationTypeEnum, eventRow: EventRow) {
    const isTemplateEmpty = (tmpl: NotificationTemplate | undefined): boolean => {
      if (!tmpl) return true;
      const content = tmpl.template || '';
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      return textContent.length === 0;
    };

    // For super users: check if an empty global template exists and edit it instead of creating duplicate
    if (this.isGlobalAdmin) {
      const existingGlobalTemplate = this.allTemplates.find(
        (t) => t.notificationEventId === eventRow.eventId && t.notificationTypeId === typeId && !t.accountId, // Global template
      );

      if (existingGlobalTemplate && isTemplateEmpty(existingGlobalTemplate)) {
        // Edit the existing empty global template
        const dlg = this.baseService.dialogService.pngDialogService;
        const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
          notificationTemplate: existingGlobalTemplate,
          isEditable: true,
          accountId: this.accountId,
          dbSaveService: this.dbSaveService,
        });
        if (ok) {
          await this.dbSaveService.saveSelectedChanges([existingGlobalTemplate]);
          this.buildEventRows();
          this.gridOptions.apiX?.setGridOption('rowData', this.eventRows);
        } else {
          existingGlobalTemplate.entityAspect.rejectChanges();
        }
        return;
      }
    }

    // Create a new template
    // Super users create global templates (accountId = undefined)
    // Regular account admins create account-specific templates
    const accountIdValue = this.isGlobalAdmin ? undefined : this.accountId;

    const template = this.dbSaveService.createEntity(NotificationTemplate, {
      accountId: accountIdValue,
      notificationEventId: eventRow.eventId,
      notificationTypeId: typeId,
    });
    await this.editNewEventTemplate(template, eventRow, typeId);
  }

  /** Edit a new event template and update the EventRow */
  private async editNewEventTemplate(template: NotificationTemplate, eventRow: EventRow, typeId: NotificationTypeEnum) {
    const dlg = this.baseService.dialogService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
      notificationTemplate: template,
      isNew: true,
      accountId: this.accountId,
      dbSaveService: this.dbSaveService,
    });

    if (ok) {
      await this.dbSaveService.saveSelectedChanges([template]);
      await this.loadTemplates(); // Reload all templates and rebuild rows
    } else {
      EntityFns.deleteOrDetach(template.entityAspect);
    }
  }

  /** Edit or create an Email Wrapper template (Header or Footer) */
  async onEditWrapperTemplate(eventId: NotificationEventEnum) {
    const isHeader = eventId === NotificationEventEnum.EmailHeader;
    const existingTemplate = isHeader ? this.emailHeaderTemplate : this.emailFooterTemplate;

    if (existingTemplate) {
      const isGlobal = !existingTemplate.accountId;

      if (isGlobal && !this.isGlobalAdmin) {
        // Non-super users editing global template should create account-specific override
        const override = this.dbSaveService.createEntity(NotificationTemplate, {
          accountId: this.accountId,
          notificationEventId: eventId,
          notificationTypeId: NotificationTypeEnum.Email, // Wrapper templates are Email only
          template: existingTemplate.template, // Copy the global template content as starting point
        });
        await this.editNewWrapperTemplate(override);
      } else {
        // Edit the existing template directly
        const dlg = this.baseService.dialogService.pngDialogService;
        const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
          notificationTemplate: existingTemplate,
          isEditable: true,
          accountId: this.accountId,
          dbSaveService: this.dbSaveService,
        });
        if (ok) {
          await this.dbSaveService.saveSelectedChanges([existingTemplate]);
          this.buildEventRows(); // Refresh wrapper template references
        } else {
          existingTemplate.entityAspect.rejectChanges();
        }
      }
    } else {
      // No template exists - create a new one
      // Super users create global templates (accountId = undefined)
      // Regular account admins create account-specific templates
      const template = this.dbSaveService.createEntity(NotificationTemplate, {
        accountId: this.isGlobalAdmin ? undefined : this.accountId,
        notificationEventId: eventId,
        notificationTypeId: NotificationTypeEnum.Email, // Wrapper templates are Email only
      });
      await this.editNewWrapperTemplate(template);
    }
  }

  /** Edit a new wrapper template */
  private async editNewWrapperTemplate(template: NotificationTemplate) {
    const dlg = this.baseService.dialogService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
      notificationTemplate: template,
      isNew: true,
      accountId: this.accountId,
      dbSaveService: this.dbSaveService,
    });

    if (ok) {
      await this.dbSaveService.saveSelectedChanges([template]);
      await this.loadTemplates(); // Reload all templates to refresh wrapper references
    } else {
      EntityFns.deleteOrDetach(template.entityAspect);
    }
  }

  /** Check if a wrapper template is global */
  isWrapperTemplateGlobal(eventId: NotificationEventEnum): boolean {
    const template = eventId === NotificationEventEnum.EmailHeader ? this.emailHeaderTemplate : this.emailFooterTemplate;
    return !template?.accountId;
  }

  /** Check if a wrapper template exists */
  hasWrapperTemplate(eventId: NotificationEventEnum): boolean {
    const template = eventId === NotificationEventEnum.EmailHeader ? this.emailHeaderTemplate : this.emailFooterTemplate;

    if (!template) return false;

    // If it's a global template, check if it's empty
    if (!template.accountId) {
      const content = template.template || '';
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      return textContent.length > 0;
    }

    return true; // Account-specific templates always count as "has template"
  }

  /** Delete an account-specific wrapper template (reverts to global) */
  async onDeleteWrapperTemplate(eventId: NotificationEventEnum) {
    const isHeader = eventId === NotificationEventEnum.EmailHeader;
    const template = isHeader ? this.emailHeaderTemplate : this.emailFooterTemplate;
    const templateName = isHeader ? 'Email Header' : 'Email Footer';

    if (!template || !template.accountId) {
      return; // Can only delete account-specific templates
    }

    const ok = await this.baseService.dialogService.askIfOk(
      'Delete Account-Specific Template',
      `Are you sure you want to delete the account-specific ${templateName} template? This will revert to using the global template.`,
    );

    if (ok) {
      EntityFns.deleteOrDetach(template.entityAspect);
      await this.dbSaveService.saveSelectedChanges([template]);
      await this.loadTemplates(); // Reload to refresh with global template
    }
  }

  async onAdd() {
    // Super users create global templates (accountId = undefined)
    // Regular account admins create account-specific templates
    const template = this.dbSaveService.createEntity(NotificationTemplate, {
      accountId: this.isGlobalAdmin ? undefined : this.accountId,
    });
    if (this.templateType.id == TemplateTypeEnum.FreeForm) {
      template.notificationEventId = NotificationEventEnum.FreeForm;
    }
    await this.editNew(template);
  }

  private async editNew(notificationTemplate: NotificationTemplate) {
    const dlg = this.baseService.dialogService.pngDialogService;
    const ok = await NotificationTemplateDialogComponent.openDialog(dlg, {
      notificationTemplate,
      isNew: true,
      accountId: this.accountId,
      dbSaveService: this.dbSaveService,
    });

    if (ok) {
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      await this.loadTemplates();
    } else {
      EntityFns.deleteOrDetach(notificationTemplate.entityAspect);
    }
    this.gridOptions.apiX?.redrawRows();
  }

  canCopy() {
    const rows = this.gridOptions.apiX?.getSelectedRows();
    return rows && rows.length == 1;
  }

  async onCopy() {
    const rows = this.gridOptions.apiX?.getSelectedRows();
    if (rows && rows.length) {
      const old = rows[0] as NotificationTemplate;
      const props = EntityFns.cloneStruct(old, ['id', 'rowVersion']) as any;
      props.accountId = this.accountId;
      // TODO - change event or type so we don't create duplicate template
      const template = this.dbSaveService.createEntity(NotificationTemplate, props);
      template.notificationTypeId = <any>undefined;
      (template.accountId = this.isGlobalAdmin ? undefined : this.accountId), await this.editNew(template);
    }
  }

  canDelete(notificationTemplate: NotificationTemplate) {
    return notificationTemplate.accountId != null;
  }

  /** Re-applies the FreeForm filter when the user toggles "Show Inactive Templates". */
  onShowInactiveChange() {
    if (this.templateType.id === TemplateTypeEnum.FreeForm) {
      this.onTemplateTypeChange();
    }
  }

  /** Active/Inactive toggle is offered only on account-specific free-form templates. */
  canToggleActive(notificationTemplate: NotificationTemplate) {
    return notificationTemplate.accountId != null && notificationTemplate.notificationEventId === NotificationEventEnum.FreeForm;
  }

  /** Flip IsActive on a free-form template, save, and re-apply the filter. */
  async onToggleActive(notificationTemplate: NotificationTemplate) {
    // TODO: drop the `any` cast once the Breeze client model is regenerated with the isActive field.
    const nt = notificationTemplate as any;
    nt.isActive = !nt.isActive;
    await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
    // Re-apply the filter so a newly-deactivated row disappears when showInactive is off.
    this.onTemplateTypeChange();
  }

  async onDelete(notificationTemplate: NotificationTemplate) {
    // if (row.isPreset) {
    //   this.toastr.warning(
    //     'Cannot delete a preset communication',
    //     'Unable to Proceed'
    //   );
    // }
    const name = notificationTemplate.templateName ?? NotificationEventEnum[notificationTemplate.notificationEventId!];
    const ok = await this.baseService.dialogService.askIfOk('Delete Template', 'Are you sure you want to delete template "' + name + '"?');
    if (ok) {
      EntityFns.deleteOrDetach(notificationTemplate.entityAspect);
      await this.dbSaveService.saveSelectedChanges([notificationTemplate]);
      await this.loadTemplates();
    }
  }

  /** Download template as json */
  // download(notificationTemplate: NotificationTemplate) {
  //   const json = JSON.stringify(EntityFns.cloneStruct(notificationTemplate, ['id','rowVersion']));
  //   UtilFns.downloadFile(json, 'application/json', 'template.json');
  // }

  /** Download selected templates as json */
  downloadSelected() {
    const selected = this.gridOptions.apiX?.getSelectedRows();
    if (!selected || !selected.length) {
      return;
    }

    let templates: NotificationTemplate[] = [];

    if (this.templateType.id === TemplateTypeEnum.Event) {
      // Event-Centric view: selected rows are EventRow objects
      // Extract the actual NotificationTemplate entities from each EventRow
      for (const eventRow of selected as EventRow[]) {
        if (eventRow.emailTemplate) templates.push(eventRow.emailTemplate);
        if (eventRow.smsTemplate) templates.push(eventRow.smsTemplate);
        if (eventRow.inAppTemplate) templates.push(eventRow.inAppTemplate);
      }
    } else {
      // FreeForm view: selected rows are NotificationTemplate entities directly
      templates = selected as NotificationTemplate[];
    }

    if (!templates.length) {
      this.toastr.warning('No templates found in the selected rows');
      return;
    }

    const structs = templates.map((x) => EntityFns.cloneStruct(x, ['id', 'rowVersion']));
    const json = JSON.stringify(structs);
    UtilFns.downloadFile(json, 'application/json', 'templates.json');
  }

  /** Parse uploaded json, create and save entities */
  async uploadHander(event: FileUploadHandlerEvent) {
    const file = event.files.shift();
    if (!file) {
      return;
    }
    const data = await file.text();
    let config = JSON.parse(data);
    if (!Array.isArray(config)) {
      config = [config];
    }

    for (const c of config) {
      if (c.accountId) {
        // if it was an account-specific template, assign it to this account.
        c.accountId = this.accountId;
      }
      if (!this.isDuplicate(c)) {
        const ent = this.dbSaveService.createEntity(NotificationTemplate, c);
        this.templates.push(ent);
      }
    }
    await this.saveChanges();
    this.gridOptions.apiX?.setGridOption('rowData', this.templates);
  }

  private isDuplicate(c: Partial<NotificationTemplate>) {
    if (
      this.templates.some(
        (x) => x.accountId == c.accountId && x.notificationEventId == c.notificationEventId && x.notificationTypeId == c.notificationTypeId,
      )
    ) {
      this.toastr.warning(
        `Omitting template for ${NotificationEventEnum[c.notificationEventId!]} ${NotificationTypeEnum[c.notificationTypeId!]} because one already exists`,
      );
      return true;
    }
    return false;
  }
}
